import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter } from "lucide-react";
import ProductCard from "@/components/common/ProductCard";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/data/constants";

export default function CommoditiesPage() {
  const [params, setParams] = useSearchParams();
  const initialCategory = params.get("category") || "All";
  const [category, setCategory] = useState(initialCategory);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Commodities — Aaha Kanakam Traders";
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category !== "All") p.category = category;
    if (search) p.search = search;
    api.get("/products", { params: p })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category, search]);

  const tabs = useMemo(() => ["All", ...CATEGORIES], []);

  const setCat = (c) => {
    setCategory(c);
    if (c === "All") setParams({});
    else setParams({ category: c });
  };

  return (
    <div data-testid="commodities-page">
      <section className="bg-forest text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="eyebrow text-gold mb-3">Catalogue</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">Agricultural Commodities</h1>
          <p className="mt-4 max-w-2xl text-white/70">Browse our sourcing catalogue. Availability may vary by season and buyer requirement — request a quote for confirmed pricing and specifications.</p>
        </div>
      </section>

      <section className="py-10 bg-ivory border-b border-borderSubtle sticky top-20 z-30 backdrop-blur-md bg-ivory/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slateneutral" />
              <input
                data-testid="commodities-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search commodities, varieties…"
                className="w-full pl-10 pr-4 py-3 text-sm border border-borderSubtle bg-white focus:outline-none focus:border-forest"
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-slateneutral">
              <Filter size={14} /> {products.length} results
            </div>
          </div>
          <div className="mt-5 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {tabs.map((t) => (
              <button
                key={t}
                data-testid={`category-tab-${t.toLowerCase().split(" ")[0]}`}
                onClick={() => setCat(t)}
                className={`whitespace-nowrap px-4 py-2 text-xs uppercase tracking-widest font-semibold border transition ${
                  category === t ? "bg-forest text-white border-forest" : "bg-white text-charcoal border-borderSubtle hover:border-forest"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-ivory min-h-[400px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {loading ? (
            <div className="text-center py-20 text-slateneutral">Loading commodities…</div>
          ) : products.length === 0 ? (
            <div data-testid="empty-state" className="text-center py-20">
              <div className="font-serif text-2xl text-forest mb-2">No commodities found</div>
              <p className="text-sm text-slateneutral">Try a different search or category. Additional commodities can be sourced on request.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
